import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  MoreVertical,
  Eye,
  RefreshCw,
  CornerDownLeft,
  CirclePlus,
  Loader2,
} from "lucide-react";
import { apiService } from '@/services/core/api.service';
import { usePermissions, ALMACEN_PERMISSIONS } from '@/shared/hooks/usePermissions';
import { PermissionGuard } from '@/shared/components';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import FilterSelect from '@/shared/components/FilterSelect';
import { EsqueletoTabla } from '@/shared/components/Esqueleto';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";

// ─── Backend types ────────────────────────────────────────────────────────────

type BackendStatus = "good" | "new_equipment" | "damaged" | "missing" | "in_maintenance";

interface HerramientaApi {
  id: string;
  name: string;
  serialCode: string;
  status: BackendStatus;
  categoryId: string;
  categoryName: string | null;
  warehouseId: string;
  warehouseName: string | null;
  currentMovementId: string | null;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  projectId: string | null;
  projectName: string | null;
  photoUrl1?: string;
  photoUrl2?: string;
  photoUrl3?: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedHerramientas {
  data: HerramientaApi[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

// ─── Types ────────────────────────────────────────────────────────────────────

type Disponibilidad  = "Disponible" | "Asignada";
type EstadoAsignacion =
  | "En buen estado"
  | "Equipo nuevo"
  | "Falla/daño"
  | "Faltante"
  | "En mantenimiento";

interface AsignacionRow {
  id: string;
  herramienta: string;
  responsable: string;
  proyectoAsignado: string;
  almacen: string;
  fechaHora: string;
  disponibilidad: Disponibilidad;
  estado: EstadoAsignacion;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────

function toDisponibilidad(h: HerramientaApi): Disponibilidad {
  // Una herramienta está asignada cuando tiene un movimiento activo
  return h.currentMovementId === null ? "Disponible" : "Asignada";
}

function toEstado(status: BackendStatus): EstadoAsignacion {
  const map: Record<BackendStatus, EstadoAsignacion> = {
    good:           "En buen estado",
    new_equipment:  "Equipo nuevo",
    damaged:        "Falla/daño",
    missing:        "Faltante",
    in_maintenance: "En mantenimiento",
  };
  // Respaldo por si el back devuelve un estado que el front no conoce (ej. un
  // bad_delivery de un ambiente donde todavía no corre la migración que lo
  // eliminó): sin esto la pastilla saldría vacía y sin color.
  return map[status] ?? "En buen estado";
}

function formatFecha(iso: string): string {
  try {
    const d = new Date(iso);
    const yyyy = d.getFullYear();
    const mm   = String(d.getMonth() + 1).padStart(2, "0");
    const dd   = String(d.getDate()).padStart(2, "0");
    const hh   = String(d.getHours()).padStart(2, "0");
    const min  = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  } catch {
    return iso;
  }
}

function toRow(h: HerramientaApi): AsignacionRow {
  return {
    id:               h.id,
    herramienta:      h.name,
    responsable:      h.responsibleUserName ?? "Sin responsable",
    proyectoAsignado: h.projectName ?? "Sin proyecto",
    almacen:          h.warehouseName ?? "Sin almacén",
    fechaHora:        formatFecha(h.updatedAt),
    disponibilidad:   toDisponibilidad(h),
    estado:           toEstado(h.status),
  };
}

// ─── API calls ────────────────────────────────────────────────────────────────

const fetchHerramientas = (page = 1, limit = 100): Promise<PaginatedHerramientas> =>
  apiService.request<PaginatedHerramientas>(`/almacen/herramientas?page=${page}&limit=${limit}`);

// ─── Actions menu ─────────────────────────────────────────────────────────────

const ActionsMenu = ({
  isAssigned,
  canReassign,
  onVer,
  onReasignar,
  onDevolver,
}: {
  isAssigned: boolean;
  canReassign: boolean;
  onVer?: () => void;
  onReasignar?: () => void;
  onDevolver?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (btnRef.current && !btnRef.current.closest("[data-actions-menu]")?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const items = [
    { label: "Ver", icon: Eye, action: onVer },
    // Asignar/Reasignar solo si la herramienta está en condiciones de usarse
    ...(canReassign
      ? [{ label: isAssigned ? "Reasignar" : "Asignar", icon: RefreshCw, action: onReasignar }]
      : []),
    // "Devolver" solo aplica si la herramienta está asignada a alguien
    ...(isAssigned
      ? [{ label: "Devolver", icon: CornerDownLeft, action: onDevolver }]
      : []),
  ];

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      // Altura estimada del menú: ~37px por item + padding vertical
      const estHeight = items.length * 37 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      // Si no cabe abajo, abrir hacia arriba (clamping al borde superior)
      const top =
        spaceBelow < estHeight + 8
          ? Math.max(8, rect.top - estHeight - 4)
          : rect.bottom + 4;
      setMenuPos({ top, left: rect.right - 176 });
    }
    setOpen((v) => !v);
  };

  return (
    <div data-actions-menu="">
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <MoreVertical className="w-4 h-4" style={{ color: "#315FB9" }} />
      </button>
      {open && (
        <div
          className="fixed w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-[9999] py-1"
          style={{ top: menuPos.top, left: menuPos.left }}
        >
          {items.map(({ label, icon: Icon, action }) => (
            <button
              key={label}
              onClick={() => { action?.(); setOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Icon className="w-4 h-4" style={{ color: "#315FB9" }} />
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Badges ───────────────────────────────────────────────────────────────────

const DisponibilidadBadge = ({ value }: { value: Disponibilidad }) => {
  const colores: Record<Disponibilidad, ColorDeBadge> = {
    Disponible: "verde",
    Asignada: "azul",
  };
  return <Badge color={colores[value]}>{value}</Badge>;
};

const EstadoBadge = ({ estado }: { estado: EstadoAsignacion }) => {
  const colores: Record<EstadoAsignacion, ColorDeBadge> = {
    "En buen estado": "verde",
    "Equipo nuevo": "azul",
    "Falla/daño": "rojo",
    "Faltante": "amarillo",
    "En mantenimiento": "amarillo",
  };
  return <Badge color={colores[estado]}>{estado}</Badge>;
};

// ─── Empty / loading / error states ──────────────────────────────────────────

const NoDataState = () => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <img src="/herramientas-empty-state.svg" alt="Sin asignaciones" className="w-40 h-auto mb-2" />
    <p className="text-base font-semibold text-gray-700">Aún no hay datos para mostrar</p>
    <p className="text-sm text-gray-400">Empieza asignando una herramienta.</p>
  </div>
);

const NoResultsState = () => (
  <div className="flex flex-col items-center justify-center py-16 gap-2">
    <p className="text-sm font-medium text-gray-500">Sin resultados</p>
    <p className="text-sm text-gray-400">Intenta con otros filtros.</p>
  </div>
);

// A-01: silueta en vez de spinner, la pantalla no cambia de forma al cargar.
const LoadingState = () => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
    <EsqueletoTabla filas={6} columnas={5} />
  </div>
);

const ErrorState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
  <div className="flex flex-col items-center justify-center py-20 gap-3">
    <p className="text-sm font-medium text-red-600">Error al cargar los datos</p>
    <p className="text-sm text-gray-400">{message}</p>
    <button
      onClick={onRetry}
      className={estiloDeBoton('primario', 'mt-2')}
    >
      Reintentar
    </button>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const AsignacionHerramientas = ({ vista = "asignacion" }: { vista?: "asignacion" | "devolucion" }) => {
  const esDevolucion = vista === "devolucion";
  // Las acciones de cada fila deben quedarse en el listado donde estas. Antes
  // apuntaban siempre a /almacen/asignacion, asi que desde Devolucion el menu
  // lateral se marcaba en Asignacion y el boton de volver regresaba alli.
  const base = esDevolucion ? "/almacen/devolucion" : "/almacen/asignacion";
  // ── All hooks first ────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const { hasPermission, isLoading: permsLoading } = usePermissions();

  // Igual que en Herramientas: el back acepta "ver" o "ver_asignadas", así que
  // checar solo "ver" dejaba fuera a quien únicamente ve las suyas.
  const canVer       =
    hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_VER) ||
    hasPermission(ALMACEN_PERMISSIONS.HERRAMIENTAS_VER_ASIGNADAS);
  const canReasignar = hasPermission(ALMACEN_PERMISSIONS.MOVIMIENTOS_REASIGNAR);
  const canDevolver  = hasPermission(ALMACEN_PERMISSIONS.MOVIMIENTOS_DEVOLVER);
  const [search, setSearch]                             = useState("");
  const [filterProyecto, setFilterProyecto]             = useState("");
  const [filterAlmacen, setFilterAlmacen]               = useState("");
  // En la vista de Devolución, pre-filtra a las herramientas asignadas (las que se pueden devolver)
  const [filterDisponibilidad, setFilterDisponibilidad] = useState(esDevolucion ? "Asignada" : "Disponible");
  const [filterResponsable, setFilterResponsable]       = useState("");
  const [filterEstado, setFilterEstado]                 = useState("");

  const [herramientas, setHerramientas] = useState<AsignacionRow[]>([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);

  const loadHerramientas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchHerramientas();
      const items: HerramientaApi[] = (res.data ?? [])
        .filter((h) => h.status !== "in_maintenance")
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
      setHerramientas(items.map(toRow));
    } catch (err: unknown) {
      const msg =
        typeof err === "object" && err !== null && "message" in err
          ? String((err as { message: unknown }).message)
          : "Error desconocido";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHerramientas();
  }, [loadHerramientas]);

  const handleLimpiar = () => {
    setSearch("");
    setFilterProyecto("");
    setFilterAlmacen("");
    setFilterDisponibilidad("");
    setFilterResponsable("");
    setFilterEstado("");
  };

  const rows = useMemo(
    () =>
      herramientas.filter((r) => {
        const q = search.toLowerCase();
        const matchSearch =
          !q ||
          r.herramienta.toLowerCase().includes(q) ||
          r.responsable.toLowerCase().includes(q) ||
          r.proyectoAsignado.toLowerCase().includes(q) ||
          r.almacen.toLowerCase().includes(q);
        return (
          matchSearch &&
          (!filterProyecto       || r.proyectoAsignado === filterProyecto) &&
          (!filterAlmacen        || r.almacen          === filterAlmacen) &&
          (!filterDisponibilidad || r.disponibilidad   === filterDisponibilidad) &&
          (!filterResponsable    || r.responsable      === filterResponsable) &&
          (!filterEstado         || r.estado           === filterEstado)
        );
      }),
    [herramientas, search, filterProyecto, filterAlmacen, filterDisponibilidad, filterResponsable, filterEstado]
  );

  // ── Derived ────────────────────────────────────────────────────────────────
  const hasData = herramientas.length > 0;

  const proyectoOptions = useMemo(
    () => [...new Set(herramientas.map((r) => r.proyectoAsignado).filter((v) => v !== "Sin proyecto"))],
    [herramientas]
  );
  const almacenOptions = useMemo(
    () => [...new Set(herramientas.map((r) => r.almacen).filter((v) => v !== "Sin almacén"))],
    [herramientas]
  );
  const responsableOptions = useMemo(
    () => [...new Set(herramientas.map((r) => r.responsable).filter((v) => v !== "Sin responsable"))],
    [herramientas]
  );

  // Mientras cargan los permisos, mostrar loader (evita el flash de "sin
  // permisos" antes de que se resuelvan los permisos del usuario).
  if (permsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#253579]" />
      </div>
    );
  }

  if (!canVer) return <PermissionsDenyPage />;

  return (
    <div className="mx-auto px-8 py-8">

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {esDevolucion ? "Devolución" : "Asignación"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {esDevolucion
              ? "Registra la devolución de una herramienta al almacén"
              : "Asigna una herramienta a un trabajador y proyecto"}
          </p>
        </div>
        <PermissionGuard
          permission={
            esDevolucion
              ? ALMACEN_PERMISSIONS.MOVIMIENTOS_DEVOLVER
              : ALMACEN_PERMISSIONS.MOVIMIENTOS_ASIGNAR
          }
        >
          <button
            onClick={() =>
              navigate(
                esDevolucion
                  ? '/almacen/devolucion/devolver'
                  : '/almacen/asignacion/asignar',
              )
            }
            className={estiloDeBoton('primario')}
          >
            <CirclePlus className="w-4 h-4" />
            {esDevolucion ? "Devolver herramienta" : "Asignar herramienta"}
          </button>
        </PermissionGuard>
      </div>

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={loadHerramientas} />
      ) : !hasData ? (
        <NoDataState />
      ) : (
        <>
          {/* Filter bar */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap justify-start items-center gap-3 mb-6">
            <div className="relative w-[260px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en herramientas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
              />
            </div>
            <FilterSelect
              label="Proyecto asignado"
              options={(proyectoOptions).map((o) => ({ value: o, label: o }))}
              value={filterProyecto}
              onChange={setFilterProyecto}
            ancho="contenido"
          />
            <FilterSelect
              label="Almacén"
              options={(almacenOptions).map((o) => ({ value: o, label: o }))}
              value={filterAlmacen}
              onChange={setFilterAlmacen}
            ancho="contenido"
          />
            <FilterSelect
              label="Disponibilidad"
              options={(["Disponible", "Asignada"]).map((o) => ({ value: o, label: o }))}
              value={filterDisponibilidad}
              onChange={setFilterDisponibilidad}
            ancho="contenido"
          />
            <FilterSelect
              label="Responsable"
              options={(responsableOptions).map((o) => ({ value: o, label: o }))}
              value={filterResponsable}
              onChange={setFilterResponsable}
            ancho="contenido"
          />
            <FilterSelect
              label="Estado"
              options={(["En buen estado", "Falla/daño", "Faltante", "En mantenimiento"]).map((o) => ({ value: o, label: o }))}
              value={filterEstado}
              onChange={setFilterEstado}
            ancho="contenido"
          />
            <button
              onClick={handleLimpiar}
              className="text-sm font-semibold text-[#315FB9] hover:underline"
            >
              Limpiar
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr style={{ backgroundColor: "#FAF9FF", borderBottom: "1px solid #E4E4E4" }}>
                    {["Herramientas", "Responsable", "Proyecto asignado", "Almacén", "Fecha y hora", "Disponibilidad", "Estado", "Acciones"].map((h) => (
                      <th
                        key={h}
                        className="text-left py-3 px-3 text-sm font-semibold first:pl-6 last:pr-4 last:text-right whitespace-nowrap"
                        style={{ color: "#534D59" }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={8}>
                        <NoResultsState />
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => navigate(`${base}/${row.id}/ver`)}
                        className="hover:bg-[#F4F7FB] transition-colors cursor-pointer"
                        style={{ borderBottom: "1px solid #E4E4E4" }}
                      >
                        <td className="py-3 px-3 pl-6 text-gray-700 font-medium whitespace-nowrap">{row.herramienta}</td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{row.responsable}</td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{row.proyectoAsignado}</td>
                        <td className="py-3 px-3 text-gray-600 whitespace-nowrap">{row.almacen}</td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">{row.fechaHora}</td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <DisponibilidadBadge value={row.disponibilidad} />
                        </td>
                        <td className="py-3 px-3 whitespace-nowrap">
                          <EstadoBadge estado={row.estado} />
                        </td>
                        <td className="py-3 px-3 pr-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <ActionsMenu
                            isAssigned={row.disponibilidad === "Asignada"}
                            canReassign={
                              row.estado === "En buen estado" ||
                              row.estado === "Equipo nuevo"
                            }
                            onVer={() => navigate(`${base}/${row.id}/ver`)}
                            onReasignar={canReasignar ? () => navigate(`${base}/${row.id}/reasignar`) : undefined}
                            onDevolver={canDevolver ? () => navigate(`${base}/${row.id}/devolver`) : undefined}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AsignacionHerramientas;
